import icn_line_chart from "assets/images/icons/icn_line_chart.png";
import { ReactComponent as Notice } from "assets/images/icons/btn_notice.svg";

import React, { useMemo, useState, useEffect } from "react";
import { useTheme } from "@mui/material/styles";
import { Box, Typography } from "@mui/material";
import { createColumnHelper } from "@tanstack/react-table";
import { v4 as uuidv4 } from "uuid";

import useTableInstance from "hooks/useTableInstance";
import useDataSourceManager from "hooks/useDataSourceManager";

import CollapseCard from "components/Collapse";
import TableField from "components/Table";
import { OutlineButton } from "components/buttons/OutlineButton";
import { SolidButton } from "components/buttons/SolidButton";

import WiredCongestionLevelSliderChart from "pages/AInsight/LinkIntegrity/charts/WiredCongestionLevelSliderChart";

const BottomActionBar = ({
  parentId,
  show,
  onCheckFormValid,
  onCancel,
  onApply,
}) => {
  useEffect(() => {
    const parentElement = document.getElementById(parentId);
    const bottomActionBarElement = document.getElementById("bottomActionBar");

    if (!parentElement || !bottomActionBarElement) return;

    const updateWidth = () => {
      bottomActionBarElement.style.width = `${parentElement.clientWidth - 32}px`;
    };

    const resizeObserver = new ResizeObserver(updateWidth);
    resizeObserver.observe(parentElement);
    updateWidth();

    return () => {
      resizeObserver.disconnect();
    };
  }, [parentId]);

  return (
    <Box
      id="bottomActionBar"
      sx={{
        position: "fixed",
        bottom: "20px",
        width: 0,
        display: show ? "flex" : "none",
        justifyContent: "center",
        gap: 2,
        margin: "16px 0",
        padding: "16px",
        backgroundColor: "primary.lightBlue",
        transform: show ? "translateY(0)" : "translateY(80px)",
        transition: "transform 0.3s ease-in-out",
        zIndex: 1300,
      }}
    >
      <OutlineButton
        onClick={onCancel}
        sx={{ marginRight: "20px" }}
        width="100px"
        height="32px"
      >
        Cancel
      </OutlineButton>

      <SolidButton
        onClick={onApply}
        width="100px"
        height="32px"
        disabled={!onCheckFormValid()}
      >
        Apply
      </SolidButton>
    </Box>
  );
};

const WiredCongestionLevelDrawer = ({
  wiredCongestionChartSeriesData,
  wiredCongestionPeakData,
  wiredCongestionApiChartData,
  auth,
  onRefreshWiredCongestion,
}) => {
  const theme = useTheme();
  const { sendRequest } = useDataSourceManager();

  const [showAnalysisCards, setShowAnalysisCards] = useState(true);
  const [activeCard, setActiveCard] = useState("peak");

  const [impactData, setImpactData] = useState([]);
  const [portData, setPortData] = useState([]);

  const [impactLoading, setImpactLoading] = useState(false);
  const [portLoading, setPortLoading] = useState(false);

  const [threshold, setThreshold] = useState(
    Number(wiredCongestionApiChartData?.threshold ?? 4),
  );
  const [editingThreshold, setEditingThreshold] = useState(
    Number(wiredCongestionApiChartData?.threshold ?? 4),
  );
  const [originalThreshold, setOriginalThreshold] = useState(
    Number(wiredCongestionApiChartData?.threshold ?? 4),
  );

  const [showBottomBar, setShowBottomBar] = useState(false);

  const columnHelper = useMemo(() => createColumnHelper(), []);

  useEffect(() => {
    const nextThreshold = Number(wiredCongestionApiChartData?.threshold ?? 4);

    setThreshold(nextThreshold);
    setEditingThreshold(nextThreshold);
    setOriginalThreshold(nextThreshold);
    setShowBottomBar(false);
  }, [wiredCongestionApiChartData?.threshold]);

  useEffect(() => {
    const devices = wiredCongestionPeakData?.devices ?? [];

    const mappedImpactData = devices.map((item) => ({
      deviceName: item.deviceName ?? "-",
      port: item.portId ?? "-",
      usage: item.usagePercent ?? null,
      drop: item.dropRatePerSec ?? "-",
      crc: item.crcErrorNum ?? "-",
    }));

    const mappedPortData = devices.map((item) => ({
      portName: `Port ${item.portId ?? "-"}`,
      status:
        item.usagePercent >= 90
          ? "Critical"
          : item.usagePercent >= 70
            ? "Warning"
            : "Normal",
      utilisation: item.usagePercent ?? null,
      drops: item.dropRatePerSec ?? "-",
      crc: item.crcErrorNum ?? "-",
    }));

    setImpactData(mappedImpactData);
    setPortData(mappedPortData);
  }, [wiredCongestionPeakData]);

  const btnStyle = {
    px: 2,
    py: 0.8,
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.white,
    borderRadius: theme.shape.customBorderRadius[6],
    fontSize: theme.typography.customFontSize[1],
    fontWeight: theme.typography.customFontWeight[3],
    cursor: "pointer",
  };

  const activeCardStyle = {
    border: `2px solid ${theme.palette.primary.main}`,
    backgroundColor: theme.palette.primary.blueFourth,
  };

  const inactiveCardStyle = {
    border: `1px solid ${theme.palette.primary.greyThread}`,
    backgroundColor: theme.palette.primary.white,
  };

  const triangleStyle = {
    position: "absolute",
    left: "50%",
    bottom: "-14px",
    transform: "translateX(-50%)",
    width: 0,
    height: 0,
    borderLeft: "7px solid transparent",
    borderRight: "7px solid transparent",
    borderBottom: `7px solid ${theme.palette.primary.greyPrimaryTransparency}`,
    zIndex: 2,
  };

  const impactColumns = useMemo(
    () => [
      columnHelper.accessor("deviceName", {
        header: "Device Name",
        cell: (info) => info.getValue() ?? "-",
      }),
      columnHelper.accessor("port", {
        header: "Port",
        cell: (info) => info.getValue() ?? "-",
      }),
      columnHelper.accessor("usage", {
        header: "Usage",
        cell: (info) =>
          info.getValue() === undefined || info.getValue() === null
            ? "-"
            : `${info.getValue()}%`,
      }),
      columnHelper.accessor("drop", {
        header: "Drop (sec)",
        cell: (info) => info.getValue() ?? "-",
      }),
      columnHelper.accessor("crc", {
        header: "CRC Errors",
        cell: (info) => info.getValue() ?? "-",
      }),
    ],
    [columnHelper],
  );

  const portColumns = useMemo(
    () => [
      columnHelper.accessor("portName", {
        header: "Port Name",
        cell: (info) => info.getValue() ?? "-",
      }),
      columnHelper.accessor("status", {
        header: "Status",
        cell: (info) => info.getValue() ?? "-",
      }),
      columnHelper.accessor("utilisation", {
        header: "Utilisation",
        cell: (info) =>
          info.getValue() === undefined || info.getValue() === null
            ? "-"
            : `${info.getValue()}%`,
      }),
      columnHelper.accessor("drops", {
        header: "Drops",
        cell: (info) => info.getValue() ?? "-",
      }),
      columnHelper.accessor("crc", {
        header: "CRC",
        cell: (info) => info.getValue() ?? "-",
      }),
    ],
    [columnHelper],
  );

  useEffect(() => {
    if (activeCard === "peak") {
      setImpactLoading(false);
    }

    if (activeCard === "most") {
      setPortLoading(false);
    }
  }, [activeCard]);

  const impactTable = useTableInstance({
    tableName: "impactAnalysisTable",
    columns: impactColumns,
    data: impactData,
    enableSorting: false,
    enablePagination: false,
  });

  const portTable = useTableInstance({
    tableName: "portAnalysisTable",
    columns: portColumns,
    data: portData,
    enableSorting: false,
    enablePagination: false,
  });

  const handleThresholdChange = (value) => {
    setEditingThreshold(value);
    setShowBottomBar(value !== originalThreshold);
    setShowAnalysisCards(true);
    setActiveCard("peak");
  };
  const handleCancel = () => {
    setEditingThreshold(originalThreshold);
    setShowBottomBar(false);
  };

  const handleApply = async () => {
    try {
      const apiPath = auth?.orgs?.id
        ? "/ents/:entId/orgs/:orgId/sites/:siteId/linkIntegrity/wiredCongestion/threshold.update"
        : "/orgs/:orgId/sites/:siteId/linkIntegrity/wiredCongestion/threshold.update";

      const restfulParams = auth?.orgs?.id
        ? {
            entId: auth?.realm?.id,
            orgId: auth?.orgs?.id,
            siteId: auth?.sites?.id,
          }
        : {
            orgId: auth?.realm?.id,
            siteId: auth?.sites?.id,
          };

      const requestParams = {
        data: {
          threshold: editingThreshold,
        },
      };

      await sendRequest(apiPath, uuidv4(), restfulParams, requestParams);

      setThreshold(editingThreshold);
      setOriginalThreshold(editingThreshold);
      setShowBottomBar(false);

      onRefreshWiredCongestion?.(editingThreshold);
    } catch (error) {
      console.error("Failed to update wired congestion threshold:", error);
    }
  };

  const peakAverageText = useMemo(() => {
    if (!Array.isArray(wiredCongestionChartSeriesData)) return "-";

    const values = wiredCongestionChartSeriesData.map(
      ([, value]) => Number(value) || 0,
    );

    const peak = Math.max(...values);
    const average =
      values.reduce((sum, value) => sum + value, 0) / values.length;

    if (!values.length || peak === 0 || average === 0) return "-";

    return `${(peak / average).toFixed(2)}x`;
  }, [wiredCongestionChartSeriesData]);

  return (
    <Box
      id="wiredCongestionLevelDrawer"
      sx={{
        p: 3,
        display: "flex",
        flexDirection: "column",
        gap: 3,
        pb: showBottomBar ? 12 : 3,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <Box
          component="img"
          src={icn_line_chart}
          sx={{ width: 20, height: 20, mr: 1.5 }}
        />

        <Typography
          sx={{
            color: theme.palette.primary.main,
            fontSize: theme.typography.customFontSize[3],
          }}
        >
          Wired Congestion Level
        </Typography>
      </Box>

      <CollapseCard title="Wired Congestion">
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <WiredCongestionLevelSliderChart
            data={wiredCongestionChartSeriesData}
            defaultThreshold={Number(
              wiredCongestionApiChartData?.threshold ?? 4,
            )}
            externalThreshold={threshold}
            inputThreshold={editingThreshold}
            onThresholdChange={handleThresholdChange}
            wiredCongestionApiChartData={wiredCongestionApiChartData}
          />

          {showAnalysisCards && (
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 2.5,
                }}
              >
                <Box
                  onClick={() => setActiveCard("peak")}
                  sx={{
                    position: "relative",
                    borderRadius: theme.shape.customBorderRadius[1],
                    p: 2,
                    cursor: "pointer",
                    ...(activeCard === "peak"
                      ? activeCardStyle
                      : inactiveCardStyle),
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: theme.typography.customFontSize[1],
                      fontWeight: theme.typography.customFontWeight[3],
                    }}
                  >
                    Analysis
                  </Typography>

                  <Typography
                    sx={{
                      color: theme.palette.primary.main,
                      fontSize: theme.typography.customFontSize[5],
                      fontWeight: theme.typography.customFontWeight[3],
                      lineHeight: 1.3,
                    }}
                  >
                    Peak Load
                  </Typography>

                  <Typography
                    sx={{ fontSize: theme.typography.customFontSize[1] }}
                  >
                    Global average: <strong>{peakAverageText}</strong>
                  </Typography>

                  {/* {activeCard === "peak" && <Box sx={triangleStyle} />} */}
                </Box>

                <Box
                  onClick={() => setActiveCard("most")}
                  sx={{
                    position: "relative",
                    borderRadius: theme.shape.customBorderRadius[1],
                    p: 2,
                    cursor: "pointer",
                    ...(activeCard === "most"
                      ? activeCardStyle
                      : inactiveCardStyle),
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: theme.typography.customFontSize[1],
                      fontWeight: theme.typography.customFontWeight[3],
                    }}
                  >
                    Most Congested
                  </Typography>

                  <Typography
                    sx={{
                      color: theme.palette.primary.main,
                      fontSize: theme.typography.customFontSize[5],
                      fontWeight: theme.typography.customFontWeight[3],
                      lineHeight: 1.3,
                    }}
                  >
                    {wiredCongestionApiChartData?.most?.deviceName ?? "-"}
                  </Typography>

                  <Typography
                    sx={{ fontSize: theme.typography.customFontSize[1] }}
                  >
                    {wiredCongestionApiChartData?.most?.deviceId ?? "-"}
                  </Typography>

                  {/* {activeCard === "most" && <Box sx={triangleStyle} />} */}
                </Box>
              </Box>
              {/* 九月再打開 */}
              {activeCard === "peak" && (
                <>
                  {/* <Box
                    sx={{
                      mt: 1.5,
                      backgroundColor: theme.palette.primary.greyThread,
                      borderRadius: theme.shape.customBorderRadius[1],
                      p: 3,
                      color: theme.palette.primary.white,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        gap: 1.2,
                        alignItems: "flex-start",
                        mb: 1,
                      }}
                    >
                      <Box sx={{ mt: "2px" }}>
                        <Notice />
                      </Box>

                      <Typography sx={{fontWeight: theme.typography.customFontWeight[3], lineHeight: 1.4 }}>
                        Port 12 (Uplink) on SW-Core-01 is experiencing critical
                        congestion
                      </Typography>
                    </Box>

                    <Typography sx={{ fontSize: theme.typography.customFontSize[1], mb: 0.5 }}>
                      Interface is running at 98% Usage with significant packet
                      loss (500/sec).
                    </Typography>

                    <Typography sx={{ fontSize: theme.typography.customFontSize[1], mb: 2 }}>
                      Check for legitimacy of traffic surges, consider
                      configuring LACP for link aggregation, or enable Storm
                      Control to mitigate broadcast storms.
                    </Typography>

                    <Box
                      sx={{
                        display: "flex",
                        gap: 1.5,
                        justifyContent: "center",
                      }}
                    >
                      <Box sx={btnStyle}>Configure LACP</Box>
                      <Box sx={btnStyle}>Enable Storm Control</Box>
                    </Box>
                  </Box> */}

                  <Box sx={{ mt: 3 }}>
                    <Typography
                      sx={{
                        color: theme.palette.primary.main,
                        fontSize: theme.typography.customFontSize[3],
                        fontWeight: theme.typography.customFontWeight[3],
                        mb: 1.5,
                      }}
                    >
                      Impact Analysis by Severity
                    </Typography>

                    <TableField {...impactTable} loading={impactLoading} />
                  </Box>
                </>
              )}

              {/* 九月再打開 */}
              {activeCard === "most" && (
                <>
                  {/* <Box
                    sx={{
                      mt: 1.5,
                      backgroundColor: theme.palette.primary.greyThread,
                      borderRadius: theme.shape.customBorderRadius[1],
                      p: 3,
                      color: theme.palette.primary.white,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        gap: 1.2,
                        alignItems: "flex-start",
                        mb: 1,
                      }}
                    >
                      <Box sx={{ mt: "2px" }}>
                        <Notice />
                      </Box>

                      <Typography sx={{ fontWeight: theme.typography.customFontWeight[3], lineHeight: 1.4 }}>
                        Critical Saturation on Port 12
                      </Typography>
                    </Box>

                    <Typography sx={{ fontSize: theme.typography.customFontSize[1], mb: 0.5 }}>
                      Uplink Interface is operating at 99% capacity. Packet
                      drops are increasing exponentially.
                    </Typography>

                    <Typography sx={{ fontSize: theme.typography.customFontSize[1], mb: 2 }}>
                      This is likely due to a video conference burst exceeding
                      the QoS queue limit.
                    </Typography>

                    <Box
                      sx={{
                        display: "flex",
                        gap: 1.5,
                        justifyContent: "center",
                      }}
                    >
                      <Box sx={btnStyle}>Apply QoS Fix</Box>
                      <Box sx={btnStyle}>View Logs</Box>
                    </Box>
                  </Box> */}

                  <Box sx={{ mt: 3 }}>
                    <Typography
                      sx={{
                        color: theme.palette.primary.main,
                        fontSize: theme.typography.customFontSize[3],
                        fontWeight: theme.typography.customFontWeight[3],
                        mb: 1.5,
                      }}
                    >
                      Port Interface
                    </Typography>

                    <TableField {...portTable} loading={portLoading} />
                  </Box>
                </>
              )}
            </Box>
          )}
        </Box>
      </CollapseCard>

      <BottomActionBar
        parentId="wiredCongestionLevelDrawer"
        show={showBottomBar}
        onCheckFormValid={() => editingThreshold !== originalThreshold}
        onCancel={handleCancel}
        onApply={handleApply}
      />
    </Box>
  );
};

export default WiredCongestionLevelDrawer;
